import { notifyFormFieldsChanged } from '../../../shared/data/useFormTableFields';
import IconAction from '../../../shared/components/IconAction';
import React, { useEffect, useId, useState } from 'react';
import { Plus, Trash2 } from '../../../shared/assets/AppIcons';
import { FormFooterAction } from '../../../shared/components/FormActionsContext';
import {
  CreateFormDialog,
  CreateFormCancelButton,
  CreateFormSubmitButton,
} from '../../../shared/components/CreateFormDialog';
import { useOptionalSession } from '../../../app/providers/session-context';
import {
  archiveClientCustomField,
  createClientCustomField,
  listClientCustomFields,
} from '../services/client-custom-fields.service';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../shared/ui/alert-dialog';

const EMPTY_EDITOR = { label: '', type: 'TEXT', required: false, placeholder: '', options: '' };
const CLIENT_CUSTOM_FIELDS_API = {
  list: listClientCustomFields,
  create: createClientCustomField,
  archive: archiveClientCustomField,
};
const TYPE_OPTIONS = [
  ['TEXT', 'Testo breve'],
  ['TEXTAREA', 'Testo lungo'],
  ['NUMBER', 'Numero'],
  ['DATE', 'Data'],
  ['SELECT', 'Scelta singola'],
  ['BOOLEAN', 'Checkbox'],
];

function makeFieldKey(label) {
  return label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function formatValue(definition, value) {
  return definition.type === 'BOOLEAN' ? (value ? 'Sì' : 'No') : String(value ?? '');
}

function normalizeDefinitions(items) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && item.archived !== true)
    .map((item, index) => ({
      ...item,
      options: Array.isArray(item.options) ? item.options : [],
      order: Number.isFinite(Number(item.order)) ? Number(item.order) : index,
    }))
    .sort((left, right) => left.order - right.order || Number(left.id || 0) - Number(right.id || 0));
}

function FieldControl({ definition, value, onChange, idPrefix, readOnly }) {
  const id = `${idPrefix}-${definition.key}`;
  const describedBy = definition.placeholder ? `${id}-hint` : undefined;
  const commonProps = {
    id,
    name: definition.key,
    disabled: readOnly,
    required: !readOnly && definition.required,
    'aria-required': definition.required || undefined,
    'aria-describedby': describedBy,
    placeholder: definition.placeholder || undefined,
    value: value ?? '',
    onChange: (event) => onChange(event.target.value),
  };

  if (definition.type === 'BOOLEAN') {
    return (
      <input
        id={id}
        name={definition.key}
        type="checkbox"
        disabled={readOnly}
        required={!readOnly && definition.required}
        aria-required={definition.required || undefined}
        checked={Boolean(value)}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  }
  if (definition.type === 'TEXTAREA') return <textarea {...commonProps} rows={4} />;
  if (definition.type === 'SELECT') {
    return (
      <select {...commonProps}>
        <option value="">Seleziona…</option>
        {definition.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      {...commonProps}
      type={definition.type === 'NUMBER' ? 'number' : definition.type === 'DATE' ? 'date' : 'text'}
    />
  );
}

export default function ClientCustomFieldsSection({
  values = {},
  onChange = () => {},
  api = CLIENT_CUSTOM_FIELDS_API,
  formKey,
  entityId,
  readOnly = false,
}) {
  const { currentUser } = useOptionalSession();
  const role = String(currentUser?.role || '').toUpperCase();
  // TENANT_ADMIN is the legacy serialized role for the canonical workspace OWNER.
  const canManage = !readOnly && ['OWNER', 'ADMIN', 'TENANT_ADMIN'].includes(role);
  const idPrefix = `custom-field-${useId().replace(/:/g, '')}`;
  const [definitions, setDefinitions] = useState([]);
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState('');
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isSavingDefinition, setIsSavingDefinition] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  useEffect(() => {
    let active = true;
    if (!currentUser) {
      setDefinitions([]);
      return () => {
        active = false;
      };
    }
    Promise.resolve(api.list(formKey, entityId))
      .then((items) => {
        if (active) setDefinitions(normalizeDefinitions(items));
      })
      .catch((requestError) => {
        if (active && canManage) setError(requestError.message || 'Campi personalizzati non disponibili.');
      });
    return () => {
      active = false;
    };
  }, [api, canManage, entityId, formKey, currentUser?.id, currentUser?.tenantId]);

  const activeValues = definitions.filter((definition) => hasValue(values[definition.key]));
  if (readOnly && activeValues.length === 0) return null;
  if (!readOnly && !canManage && definitions.length === 0) return null;

  const updateValue = (key, value) => onChange({ ...values, [key]: value });
  const updateEditor = (key, value) => setEditor((current) => ({ ...current, [key]: value }));

  const saveDefinition = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!editor || isSavingDefinition) return;
    const label = editor.label.trim();
    const key = makeFieldKey(label);
    const options =
      editor.type === 'SELECT'
        ? editor.options
            .split('\n')
            .map((option) => option.trim())
            .filter(Boolean)
        : [];
    if (!label || key.length < 2) return setError('Inserisci un nome campo valido.');
    if (definitions.some((definition) => definition.key === key))
      return setError('Esiste già un campo con questa chiave. Scegli un nome diverso.');
    if (editor.type === 'SELECT' && options.length < 1)
      return setError('Inserisci almeno un’opzione per il campo select.');
    if (new Set(options).size !== options.length)
      return setError('Le opzioni del campo select devono essere univoche.');

    setIsSavingDefinition(true);
    setError('');
    try {
      const result = await api.create({ ...editor, label, key, options, order: definitions.length }, formKey);
      if (result?.ok === false) {
        setError(result?.message || 'Impossibile salvare il campo.');
        return;
      }
      const definition = result?.ok === true && result.data ? result.data : result;
      setDefinitions((current) => normalizeDefinitions([...current, definition]));
      setEditor(null);
      notifyFormFieldsChanged(formKey);
    } catch (requestError) {
      setError(requestError.message || 'Impossibile salvare il campo.');
    } finally {
      setIsSavingDefinition(false);
    }
  };

  const archiveDefinition = async () => {
    if (!archiveTarget || isArchiving) return;
    setIsArchiving(true);
    try {
      const result = await api.archive(archiveTarget.id, formKey);
      if (result?.ok === false) {
        setError(result?.message || 'Impossibile archiviare il campo.');
        return;
      }
      setDefinitions((current) => current.filter((item) => item.id !== archiveTarget.id));
      const remainingValues = { ...values };
      delete remainingValues[archiveTarget.key];
      onChange?.(remainingValues);
      setArchiveTarget(null);
      notifyFormFieldsChanged(formKey);
    } catch (requestError) {
      setError(requestError.message || 'Impossibile archiviare il campo.');
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <section
      className={`create-form-dialog__full clients-dialog-section custom-fields-section${!definitions.length && !error ? ' is-empty' : ''}`}
      aria-labelledby={`${idPrefix}-title`}
    >
      <div className="custom-fields-section__header">
        <h4 id={`${idPrefix}-title`}>Campi personalizzati</h4>
        {canManage ? (
          <FormFooterAction>
            <button
              type="button"
              className="custom-fields-section__add"
              aria-label="Aggiungi campo"
              title="Aggiungi campo"
              onClick={() => {
                setEditor({ ...EMPTY_EDITOR });
                setError('');
              }}
            >
              <Plus size={16} aria-hidden="true" /> <span>Aggiungi campo</span>
            </button>
          </FormFooterAction>
        ) : null}
      </div>

      {error ? (
        <p className="custom-fields-section__error wt-inline-error" role="alert">
          {error}
        </p>
      ) : null}

      {editor ? (
        <CreateFormDialog
          open
          portal
          busy={isSavingDefinition}
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
          icon={Plus}
          title="Nuovo campo personalizzato"
          footer={
            <>
              <CreateFormCancelButton onClick={() => setEditor(null)} disabled={isSavingDefinition} />
              <CreateFormSubmitButton form={`${idPrefix}-editor-form`} saving={isSavingDefinition}>
                Salva campo
              </CreateFormSubmitButton>
            </>
          }
        >
          <form
            id={`${idPrefix}-editor-form`}
            onSubmit={saveDefinition}
            className="create-form-dialog__grid custom-fields-section__editor"
            role="group"
            aria-label="Editor campo personalizzato"
          >
            <div className="create-form-dialog__field">
              <label htmlFor={`${idPrefix}-editor-label`}>
                Nome campo <span aria-hidden="true">*</span>
              </label>
              <input
                id={`${idPrefix}-editor-label`}
                value={editor.label}
                onChange={(event) => updateEditor('label', event.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="create-form-dialog__field">
              <label htmlFor={`${idPrefix}-editor-type`}>
                Tipo <span aria-hidden="true">*</span>
              </label>
              <select
                id={`${idPrefix}-editor-type`}
                value={editor.type}
                onChange={(event) => updateEditor('type', event.target.value)}
                required
              >
                {TYPE_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="create-form-dialog__field">
              <label htmlFor={`${idPrefix}-editor-placeholder`}>Placeholder</label>
              <input
                id={`${idPrefix}-editor-placeholder`}
                value={editor.placeholder}
                onChange={(event) => updateEditor('placeholder', event.target.value)}
              />
            </div>
            <div className="create-form-dialog__field custom-fields-section__checkbox-field">
              <input
                id={`${idPrefix}-editor-required`}
                type="checkbox"
                checked={editor.required}
                onChange={(event) => updateEditor('required', event.target.checked)}
              />
              <label htmlFor={`${idPrefix}-editor-required`}>Obbligatorio</label>
            </div>
            {editor.type === 'SELECT' ? (
              <div className="create-form-dialog__field create-form-dialog__full">
                <label htmlFor={`${idPrefix}-editor-options`}>
                  Opzioni, una per riga <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id={`${idPrefix}-editor-options`}
                  value={editor.options}
                  onChange={(event) => updateEditor('options', event.target.value)}
                  required
                />
              </div>
            ) : null}
            {error ? (
              <p className="custom-fields-section__error create-form-dialog__full wt-inline-error" role="alert">
                {error}
              </p>
            ) : null}
          </form>
        </CreateFormDialog>
      ) : null}

      {readOnly ? (
        <div className="create-form-dialog__grid custom-fields-section__values">
          {activeValues.map((definition) => (
            <div className="create-form-dialog__field" key={definition.id || definition.key}>
              <span className="custom-fields-section__label">{definition.label}</span>
              <strong>{formatValue(definition, values[definition.key])}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="create-form-dialog__grid custom-fields-section__fields">
          {definitions.map((definition) => {
            const controlId = `${idPrefix}-${definition.key}`;
            return (
              <div
                className={`create-form-dialog__field ${definition.type === 'TEXTAREA' ? 'create-form-dialog__full' : ''}`}
                key={definition.id || definition.key}
              >
                <div className="custom-fields-section__control-row">
                  <div className="custom-fields-section__control">
                    <label htmlFor={controlId}>
                      {definition.label} {definition.required ? <span aria-hidden="true">*</span> : null}
                    </label>
                    <FieldControl
                      definition={definition}
                      value={values[definition.key]}
                      onChange={(value) => updateValue(definition.key, value)}
                      idPrefix={idPrefix}
                      readOnly={false}
                    />
                    {definition.placeholder ? (
                      <span id={`${controlId}-hint`} className="sr-only">
                        {definition.placeholder}
                      </span>
                    ) : null}
                  </div>
                  {canManage ? (
                    <button
                      type="button"
                      className="custom-fields-section__archive"
                      aria-label={`Elimina campo ${definition.label}`}
                      onClick={() => setArchiveTarget(definition)}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog
        open={Boolean(archiveTarget)}
        onOpenChange={(open) => {
          if (!open && !isArchiving) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiviare il campo personalizzato?</AlertDialogTitle>
            <AlertDialogDescription>
              Il campo sparirà dai nuovi form e non sarà più richiesto, ma i valori storici già salvati
              saranno conservati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <IconAction asChild iconKey="actions.reset"><AlertDialogCancel disabled={isArchiving}>Annulla</AlertDialogCancel></IconAction>
            <AlertDialogAction
              className="custom-fields-section__confirm-archive"
              onClick={archiveDefinition}
              disabled={isArchiving}
            >
              {isArchiving ? 'Archiviazione…' : 'Elimina campo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
